import type { StorageProvider, UploadOptions, UploadResult } from "@/services/storage/types";

type R2StorageConfig = {
  accountId: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  publicBaseUrl: string;
  endpointBaseUrl: string;
};

const AWS_ALGORITHM = "AWS4-HMAC-SHA256";
const AWS_SERVICE = "s3";
const DEFAULT_SIGNED_URL_EXPIRY = 60 * 60;

const getEnv = (key: string): string | undefined => {
  const viteValue = typeof import.meta !== "undefined" ? import.meta.env?.[key] : undefined;
  const nodeValue = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.[key];
  return viteValue ?? nodeValue;
};

const requireAnyEnv = (...keys: string[]): string => {
  for (const key of keys) {
    const value = getEnv(key)?.trim();
    if (value) return value;
  }

  throw new Error(`Missing required Cloudflare R2 storage configuration. Set one of: ${keys.join(", ")}`);
};

const encodePathSegments = (filePath: string): string => (
  filePath
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/")
);

const toHex = (bytes: ArrayBuffer): string => (
  Array.from(new Uint8Array(bytes)).map((byte) => byte.toString(16).padStart(2, "0")).join("")
);

const textBytes = (value: string): Uint8Array => new TextEncoder().encode(value);

const hmacSha256 = async (key: Uint8Array | string, value: string): Promise<ArrayBuffer> => {
  const rawKey = typeof key === "string" ? textBytes(key) : key;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    rawKey.buffer as ArrayBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const data = textBytes(value);
  return crypto.subtle.sign("HMAC", cryptoKey, data.buffer as ArrayBuffer);
};

const sha256Hex = async (value: string): Promise<string> => {
  const data = textBytes(value);
  return toHex(await crypto.subtle.digest("SHA-256", data.buffer as ArrayBuffer));
};

const deriveSigningKey = async (secretAccessKey: string, dateStamp: string, region: string): Promise<Uint8Array> => {
  const dateKey = await hmacSha256(`AWS4${secretAccessKey}`, dateStamp);
  const regionKey = await hmacSha256(new Uint8Array(dateKey), region);
  const serviceKey = await hmacSha256(new Uint8Array(regionKey), AWS_SERVICE);
  const signingKey = await hmacSha256(new Uint8Array(serviceKey), "aws4_request");
  return new Uint8Array(signingKey);
};

const formatAmzDate = (now: Date): string => now.toISOString().replace(/[:-]|\.\d{3}/g, "");

const getR2Config = (): R2StorageConfig => {
  const accountId = requireAnyEnv("VITE_R2_ACCOUNT_ID", "R2_ACCOUNT_ID");
  const bucket = requireAnyEnv("VITE_R2_BUCKET", "R2_BUCKET");
  const accessKeyId = requireAnyEnv("VITE_R2_ACCESS_KEY_ID", "R2_ACCESS_KEY_ID");
  const secretAccessKey = requireAnyEnv("VITE_R2_SECRET_ACCESS_KEY", "R2_SECRET_ACCESS_KEY");

  const publicBaseUrl = (
    getEnv("VITE_R2_PUBLIC_BASE_URL")
    || getEnv("R2_PUBLIC_BASE_URL")
    || `https://pub-${getEnv("VITE_R2_PUBLIC_DEV_ID") || getEnv("R2_PUBLIC_DEV_ID") || accountId}.r2.dev/`
  ).replace(/\/+$/, "");

  const endpointBaseUrl = (
    getEnv("VITE_R2_ENDPOINT")
    || getEnv("R2_ENDPOINT")
    || `https://${accountId}.r2.cloudflarestorage.com/${bucket}`
  ).replace(/\/+$/, "");

  const sessionToken = getEnv("VITE_R2_SESSION_TOKEN") || getEnv("R2_SESSION_TOKEN");

  return { accountId, bucket, accessKeyId, secretAccessKey, sessionToken, publicBaseUrl, endpointBaseUrl };
};

const resolveContentType = (file: File | Blob, options?: UploadOptions): string => {
  if (options?.contentType) return options.contentType;
  return ("type" in file ? file.type : "") || "application/octet-stream";
};

const buildPublicUrl = (filePath: string): string => `${getR2Config().publicBaseUrl}/${encodePathSegments(filePath)}`;

const assertSuccess = async (response: Response, action: string, filePath: string): Promise<void> => {
  if (response.ok) return;

  const responseText = await response.text().catch(() => "");
  throw new Error(
    `Cloudflare R2 ${action} failed for ${filePath}: ${response.status} ${response.statusText}${
      responseText ? ` - ${responseText}` : ""
    }`,
  );
};

type SignOptions = {
  filePath: string;
  method: "GET" | "PUT" | "DELETE" | "HEAD";
  expiresInSeconds?: number;
  extraQuery?: Record<string, string>;
};

const signR2Url = async ({ filePath, method, expiresInSeconds = DEFAULT_SIGNED_URL_EXPIRY, extraQuery }: SignOptions): Promise<string> => {
  const config = getR2Config();
  const now = new Date();
  const amzDate = formatAmzDate(now);
  const dateStamp = amzDate.slice(0, 8);
  const region = "auto";
  const credentialScope = `${dateStamp}/${region}/${AWS_SERVICE}/aws4_request`;
  const encodedPath = encodePathSegments(filePath);
  const url = new URL(`${config.endpointBaseUrl}/${encodedPath}`);

  const query = new URLSearchParams({
    "X-Amz-Algorithm": AWS_ALGORITHM,
    "X-Amz-Credential": `${config.accessKeyId}/${credentialScope}`,
    "X-Amz-Date": amzDate,
    "X-Amz-Expires": String(expiresInSeconds),
    "X-Amz-SignedHeaders": "host",
  });

  if (config.sessionToken) query.set("X-Amz-Security-Token", config.sessionToken);
  if (extraQuery) {
    for (const [key, value] of Object.entries(extraQuery)) {
      query.set(key, value);
    }
  }

  const sortedQuery = [...query.entries()].sort(([a], [b]) => a.localeCompare(b));
  const canonicalQuery = sortedQuery
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");

  const canonicalRequest = [
    method,
    `/${config.bucket}/${encodedPath}`,
    canonicalQuery,
    `host:${url.host}\n`,
    "host",
    "UNSIGNED-PAYLOAD",
  ].join("\n");

  const stringToSign = [
    AWS_ALGORITHM,
    amzDate,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join("\n");

  const signingKey = await deriveSigningKey(config.secretAccessKey, dateStamp, region);
  const signature = toHex(await hmacSha256(signingKey, stringToSign));

  const signedQuery = new URLSearchParams(canonicalQuery);
  signedQuery.set("X-Amz-Signature", signature);

  return `${url.toString()}?${signedQuery.toString()}`;
};

export const cloudflareR2StorageProvider: StorageProvider = {
  async upload(filePath: string, file: File | Blob, options?: UploadOptions): Promise<UploadResult> {
    const uploadUrl = await signR2Url({ filePath, method: "PUT" });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": resolveContentType(file, options),
        ...(options?.cacheControl ? { "Cache-Control": options.cacheControl } : {}),
      },
      body: file,
    });

    await assertSuccess(response, "upload", filePath);
    return { filePath, publicUrl: buildPublicUrl(filePath) };
  },

  getPublicUrl(filePath: string): string {
    return buildPublicUrl(filePath);
  },

  async getSignedUrl(filePath: string, expiresInSeconds = DEFAULT_SIGNED_URL_EXPIRY): Promise<string> {
    return signR2Url({ filePath, method: "GET", expiresInSeconds });
  },

  async remove(filePaths: string[]): Promise<void> {
    await Promise.all(filePaths.map(async (filePath) => {
      const deleteUrl = await signR2Url({ filePath, method: "DELETE" });
      const response = await fetch(deleteUrl, { method: "DELETE" });

      if (response.status === 404) return;
      await assertSuccess(response, "delete", filePath);
    }));
  },

  async uploadStream(filePath: string, stream: ReadableStream<Uint8Array>, options?: UploadOptions): Promise<UploadResult> {
    const blob = await new Response(stream).blob();
    return this.upload(filePath, blob, options);
  },

  async downloadStream(filePath: string): Promise<ReadableStream<Uint8Array>> {
    const downloadUrl = await signR2Url({ filePath, method: "GET", expiresInSeconds: 300 });
    const response = await fetch(downloadUrl);

    await assertSuccess(response, "download", filePath);

    if (!response.body) {
      throw new Error(`Cloudflare R2 download returned no body for ${filePath}`);
    }

    return response.body as ReadableStream<Uint8Array>;
  },
};
